<template>
  <div class="tw-space-y-2">
    <Label :for="id" class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block">
      {{ label }}
    </Label>

    <div
      ref="dropzoneRef"
      :class="[
        'tw-relative tw-group tw-cursor-pointer tw-transition-all tw-duration-200',
        'tw-border-2 tw-border-dashed tw-rounded-lg tw-p-6',
        'tw-bg-secondary-50 hover:tw-bg-secondary-100',
        isDragOver ? 'tw-border-primary-500 tw-bg-primary-50' : 'tw-border-secondary-300',
        hasError ? 'tw-border-red-500 tw-bg-red-50' : '',
        disabled ? 'tw-opacity-50 tw-cursor-not-allowed' : 'hover:tw-border-secondary-400',
      ]"
      @click="openFileDialog"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <!-- Hidden file input -->
      <input
        :id="id"
        ref="fileInputRef"
        type="file"
        :accept="accept"
        :disabled="disabled"
        class="tw-hidden"
        @change="handleFileSelect"
      />

      <!-- Dropzone content -->
      <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-space-y-3">
        <!-- Preview or upload area -->
        <div v-if="previewUrl" class="tw-relative tw-group">
          <img
            :src="previewUrl"
            :alt="label"
            class="tw-max-w-full tw-max-h-32 tw-object-contain tw-rounded-md tw-shadow-sm"
          />
          <div
            v-if="!disabled"
            class="tw-absolute tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-rounded-md"
          >
            <Button
              variant="secondary"
              size="sm"
              @click.stop="clearFile"
              class="tw-bg-white tw-text-secondary-700 hover:tw-bg-secondary-100"
            >
              <X class="tw-w-4 tw-h-4 tw-mr-1" />
              {{ $t('common.forms.remove') }}
            </Button>
          </div>
        </div>

        <div v-else class="tw-text-center">
          <Upload
            :class="[
              'tw-w-12 tw-h-12 tw-mx-auto tw-mb-3 tw-transition-colors',
              isDragOver ? 'tw-text-primary-500' : 'tw-text-secondary-400',
              hasError ? 'tw-text-red-500' : '',
            ]"
          />
          <div class="tw-space-y-1">
            <p class="tw-text-sm tw-font-medium tw-text-secondary-700">
              {{ uploadText || $t('common.forms.click_to_upload') }}
            </p>
            <p class="tw-text-xs tw-text-secondary-500">
              {{ description || $t('common.forms.or_drag_and_drop') }}
            </p>
            <p v-if="acceptText" class="tw-text-xs tw-text-secondary-400">
              {{ acceptText }}
            </p>
          </div>
        </div>
      </div>

      <!-- Loading overlay -->
      <div
        v-if="isLoading"
        class="tw-absolute tw-inset-0 tw-bg-white tw-bg-opacity-75 tw-flex tw-items-center tw-justify-center tw-rounded-lg"
      >
        <div
          class="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-b-2 tw-border-primary-500"
        ></div>
      </div>
    </div>

    <!-- Error message -->
    <span class="tw-text-sm tw-text-red-500 tw-mt-1 tw-block tw-min-h-[20px]">
      {{ errorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Upload, X } from 'lucide-vue-next';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

interface Props {
  id?: string;
  label?: string;
  modelValue?: File | string | null;
  accept?: string;
  acceptText?: string;
  uploadText?: string;
  description?: string;
  disabled?: boolean;
  errorMessage?: string;
  maxSize?: number; // in MB
  isLoading?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: File | string | null): void;
  (e: 'fileSelected', file: File): void;
  (e: 'fileRemoved'): void;
  (e: 'error', message: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  id: 'dropzone',
  accept: 'image/*',
  acceptText: 'PNG, JPG, JPEG (max 10MB)',
  maxSize: 10,
  disabled: false,
  isLoading: false,
});

const emit = defineEmits<Emits>();

const dropzoneRef = ref<HTMLElement>();
const fileInputRef = ref<HTMLInputElement>();
const isDragOver = ref(false);

const hasError = computed(() => !!props.errorMessage);

const previewUrl = computed(() => {
  if (props.modelValue instanceof File) {
    return URL.createObjectURL(props.modelValue);
  }
  if (typeof props.modelValue === 'string' && props.modelValue) {
    return props.modelValue;
  }
  return null;
});

const openFileDialog = () => {
  if (!props.disabled && fileInputRef.value) {
    fileInputRef.value.click();
  }
};

const validateFile = (file: File): string | null => {
  // Check file size
  if (props.maxSize && file.size > props.maxSize * 1024 * 1024) {
    return `File size must be less than ${props.maxSize}MB`;
  }

  // Check file type
  if (props.accept && props.accept !== '*') {
    const acceptedTypes = props.accept.split(',').map(type => type.trim());
    const isValidType = acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      }
      if (type.includes('*')) {
        const baseType = type.split('/')[0];
        return file.type.startsWith(baseType);
      }
      return file.type === type;
    });

    if (!isValidType) {
      return `File type not supported. Accepted: ${props.acceptText || props.accept}`;
    }
  }

  return null;
};

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];

  if (file) {
    processFile(file);
  }
};

const handleDrop = (event: DragEvent) => {
  isDragOver.value = false;

  if (props.disabled) return;

  const files = event.dataTransfer?.files;
  if (files?.length) {
    processFile(files[0]);
  }
};

const handleDragOver = (event: DragEvent) => {
  if (!props.disabled) {
    isDragOver.value = true;
  }
};

const handleDragLeave = (event: DragEvent) => {
  // Only set to false if we're leaving the dropzone entirely
  const rect = dropzoneRef.value?.getBoundingClientRect();
  if (rect) {
    const { clientX, clientY } = event;
    if (
      clientX < rect.left ||
      clientX > rect.right ||
      clientY < rect.top ||
      clientY > rect.bottom
    ) {
      isDragOver.value = false;
    }
  }
};

const processFile = (file: File) => {
  const error = validateFile(file);

  if (error) {
    emit('error', error);
    return;
  }

  emit('update:modelValue', file);
  emit('fileSelected', file);
};

const clearFile = () => {
  emit('update:modelValue', null);
  emit('fileRemoved');

  // Clear the file input
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

// Clean up object URLs to prevent memory leaks
watch(
  () => props.modelValue,
  (newValue, oldValue) => {
    if (oldValue instanceof File && newValue !== oldValue) {
      const oldUrl = URL.createObjectURL(oldValue);
      URL.revokeObjectURL(oldUrl);
    }
  },
  { immediate: false }
);
</script>
